import React from 'react';
import {
  DeleteOutlined,
  ExclamationCircleFilled,
  InfoCircleOutlined,
  SmallDashOutlined,
  EyeOutlined,
  DownloadOutlined,
  EyeInvisibleOutlined,
  UserOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import HwzComponent from '../../../common/component/HwzComponent';
import SafeUtil from '../../../common/util/SafeUtil';
import DateUtil from '../../../common/util/DateUtil';
import Expanding from '../../widget/Expanding';
import Share from '../../../common/model/share/Share';
import Sun from '../../../common/model/global/Sun';
import './ShareBar.less';
import { Modal, Tooltip, Input, Row, Col, message, Tag } from 'antd';
import MessageBoxUtil from '../../../common/util/MessageBoxUtil';
import ShareOperationModal from '../../matter/widget/ShareOperationModal';
import Lang from '../../../common/model/global/Lang';
import Moon from '../../../common/model/global/Moon';

interface IProps {
  share: Share;
  onDeleteSuccess: () => any;
  showDelete?: boolean; // 是否显示删除按钮（用于区分"我分享给别人"和"别人分享给我"）
}

interface IState {
  showShareCodeInput: boolean;
  shareCode: string;
  showMore: boolean; // 添加 showMore 到状态
}

export default class ShareBar extends HwzComponent<IProps, IState> {
  // 设置默认属性
  static defaultProps = {
    showDelete: true
  };

  constructor(props: IProps) {
    super(props);
    
    this.state = {
      showShareCodeInput: false,
      shareCode: '',
      showMore: false // 初始化 showMore
    };
  }

  // 判断当前用户是否是分享者
  isShareOwner(): boolean {
    const { share } = this.props;
    const currentUser = Moon.getSingleton().user;
    return Boolean(currentUser.uuid && share.userUuid && currentUser.uuid === share.userUuid);
  }

  // 获取权限显示文本
  getPermissionText(): string {
    const { share } = this.props;
    if (share.sharePermission === 'PREVIEW_ONLY') {
      return Lang.t('share.previewOnly');
    } else {
      return Lang.t('share.fullPermission');
    }
  }

  // 获取权限标签颜色
  getPermissionColor(): string {
    const { share } = this.props;
    return share.sharePermission === 'PREVIEW_ONLY' ? 'orange' : 'green';
  }
  getShareTargetText(): string {
    const { share } = this.props;
    
    if (share.shareScope === 'PUBLIC') {
      return Lang.t('share.publicShare');
    }
    
    // 私有分享，显示分享给谁
    const targets: string[] = [];
    
    // 处理用户目标 - 直接使用数组长度
    if (share.targetUserUuids && share.targetUserUuids.length > 0) {
      targets.push(`${share.targetUserUuids.length}人`);
    }
    
    // 处理空间目标 - 直接使用数组长度
    if (share.targetSpaceUuids && share.targetSpaceUuids.length > 0) {
      targets.push(`${share.targetSpaceUuids.length}个空间`);
    }
    
    if (targets.length === 0) {
      return Lang.t('share.privateShare');
    }
    
    return targets.join('、');
  }
  // 处理分享项点击
  handleShareClick = () => {
    const { share, showDelete } = this.props;
    
    console.log('Share clicked:', share);
    console.log('Show delete:', showDelete); // 判断是哪个页面
    
    // 检查分享是否过期
    if (share.hasExpired()) {
      MessageBoxUtil.warning(Lang.t('share.expired'));
      return;
    }

    // 检查 UUID 是否存在
    if (!share.uuid) {
      MessageBoxUtil.error('分享信息不完整');
      return;
    }

    // 公开分享直接跳转
    if (share.shareScope === 'PUBLIC') {
      console.log('公开分享，跳转到详情页');
      Sun.navigateTo(`/share/detail/${share.uuid}`);
      return;
    }

    // 私有分享处理逻辑
    if (share.shareScope === 'PRIVATE') {
      // 在"我分享给别人"页面直接跳转
      if (showDelete) {
        console.log('我分享的私有分享，直接跳转');
        Sun.navigateTo(`/share/detail/${share.uuid}`);
        return;
      }
      
      // 在"别人分享给我"页面，既然能显示说明有权限，直接跳转
      console.log('别人分享给我的私有分享，直接跳转');
      Sun.navigateTo(`/share/detail/${share.uuid}`);
      return;
    }
  };

  // 处理分享码输入确认
  handleShareCodeConfirm = () => {
    const { shareCode } = this.state;
    const { share } = this.props;
    
    if (!shareCode.trim()) {
      message.warning(Lang.t('share.enterCode'));
      return;
    }
    
    // 验证分享码
    if (shareCode !== share.code) {
      message.error(Lang.t('share.codeError'));
      return;
    }
    
    // 分享码正确，跳转到详情页
    console.log('分享码正确，跳转到详情页');
    Sun.navigateTo(`/share/detail/${share.uuid}?code=${shareCode}`);
    this.setState({ showShareCodeInput: false, shareCode: '' });
  };

  // 取消分享码输入
  handleShareCodeCancel = () => {
    this.setState({ showShareCodeInput: false, shareCode: '' });
  };

  showShare() {
    const { share } = this.props;
    console.log('Show share called with share:', share);
    
    if (!share.uuid) {
      MessageBoxUtil.error('分享信息不完整');
      return;
    }
    
    if (typeof ShareOperationModal.open !== 'function') {
      console.error('ShareOperationModal.open is not a function');
      MessageBoxUtil.error('分享功能暂不可用');
      return;
    }
    
    try {
      ShareOperationModal.open(share);
    } catch (error) {
      console.error('Error opening ShareOperationModal:', error);
      MessageBoxUtil.error('打开分享详情失败');
    }
  }

  delShare() {
    Modal.confirm({
      title: Lang.t('actionCanNotRevertConfirm'),
      icon: <ExclamationCircleFilled twoToneColor="#FFDC00" />,
      onOk: () => {
        this.props.share.httpDel(() => {
          MessageBoxUtil.success(Lang.t('operationSuccess'));
          this.props.onDeleteSuccess();
        });
      },
    });
  }

  toggleHandles() {
    this.setState({ showMore: !this.state.showMore });
  }

  render() {
    const { share, showDelete } = this.props;
    const { showMore, showShareCodeInput, shareCode } = this.state;
    const isShareOwner = this.isShareOwner();
    const shareTargetText = this.getShareTargetText();
    
    return (
      <div className="widget-share-bar">
        {/* 分享码输入模态框 - 现在只会在特殊情况下使用 */}
        {showShareCodeInput && (
          <Modal
            title={Lang.t('share.enterShareCode')}
            visible={showShareCodeInput}
            onOk={this.handleShareCodeConfirm}
            onCancel={this.handleShareCodeCancel}
            okText={Lang.t('confirm')}
            cancelText={Lang.t('cancel')}
          >
            <Row>
              <Col span={24}>
                <Input
                  placeholder={Lang.t('share.enterCode')}
                  value={shareCode}
                  onChange={(e) => this.setState({ shareCode: e.target.value })}
                  onPressEnter={this.handleShareCodeConfirm}
                  size="large"
                />
              </Col>
            </Row>
          </Modal>
        )}
        
        <div
          onClick={(e) =>
            SafeUtil.stopPropagationWrap(e)(this.handleShareClick())
          }
        >
          <div className="media">
            <div className="pull-left">
              <div className="left-part">
                <span className="basic-span">
                  <img className="share-icon" src={share.getIcon()} />
                  {/* 根据权限显示不同的预览图标 */}
                  {share.sharePermission === 'PREVIEW_ONLY' && !isShareOwner ? (
                    <EyeInvisibleOutlined className="preview-hint preview-only" />
                  ) : (
                    <EyeOutlined className="preview-hint" />
                  )}
                </span>
              </div>
            </div>

            {/*在大屏下的操作栏*/}
            <div className="pull-right visible-pc">
              <div className="right-part">
                {/* 添加权限标签 */}
                <Tag color={this.getPermissionColor()} className="permission-tag">
                  {this.getPermissionText()}
                </Tag>

                <span className="share-operation">
                  <Tooltip title={Lang.t('share.shareDetail')}>
                    <InfoCircleOutlined
                      className="btn-action mr5 text-theme"
                      onClick={(e) =>
                        SafeUtil.stopPropagationWrap(e)(this.showShare())
                      }
                    />
                  </Tooltip>
                  {showDelete && (
                    <Tooltip title={Lang.t('delete')}>
                      <DeleteOutlined
                        className="btn-action text-danger"
                        onClick={(e) =>
                          SafeUtil.stopPropagationWrap(e)(this.delShare())
                        }
                      />
                    </Tooltip>
                  )}
                </span>

                <Tooltip title={Lang.t('share.shareTime')}>
                  <span className="share-date">
                    {DateUtil.simpleDateHourMinute(share.updateTime)}
                  </span>
                </Tooltip>

                <Tooltip title={Lang.t('share.expireTime')}>
                  <span className="share-date text-center">
                    {share.expireInfinity
                      ? Lang.t('share.noExpire')
                      : DateUtil.simpleDateHourMinute(share.expireTime)}
                  </span>
                </Tooltip>
              </div>
            </div>

            {/*在小屏幕下的操作栏*/}
            <div className="pull-right visible-mobile">
              <span
                className="more-btn"
                onClick={(e) =>
                  SafeUtil.stopPropagationWrap(e)(this.toggleHandles())
                }
              >
                <SmallDashOutlined className="btn-action navy f18" />
              </span>
            </div>

            <div className="media-body">
              <div className="middle-part">
                <span className="share-name">
                  {share.name}
                  {share.hasExpired() ? (
                    <span className="text-danger">
                      {Lang.t('share.expired')}
                    </span>
                  ) : null}
                  
                  {/* 分享类型标签 */}
                  <span className={`share-type-tag ${share.shareScope === 'PRIVATE' ? 'private' : 'public'}`}>
                    {share.shareScope === 'PRIVATE' ? Lang.t('share.private') : Lang.t('share.public')}
                  </span>
                  
                  {/* 添加预览提示 */}
                  <span className="preview-text">
                    {Lang.t('share.clickToPreview')}
                    {/* 如果是预览权限且不是分享者，添加额外提示 */}
                    {share.sharePermission === 'PREVIEW_ONLY' && !isShareOwner && (
                      <span className="preview-only-hint">
                        ({Lang.t('share.previewOnly')})
                      </span>
                    )}
                  </span>
                </span>
                
                {/* 添加分享对象信息 */}
                <div className="share-target-info">
                  {share.shareScope === 'PRIVATE' ? (
                    <>
                      <UserOutlined className="target-icon" />
                      <span className="target-text">{shareTargetText}</span>
                    </>
                  ) : (
                    <>
                      <TeamOutlined className="target-icon" />
                      <span className="target-text">{shareTargetText}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Expanding>
          {showMore ? (
            <div className="more-panel">
              <div className="cell-btn text">
                <span>
                  {Lang.t('share.shareTime')}:{' '}
                  {DateUtil.simpleDateHourMinute(share.createTime)}
                </span>
                <span className="ml10">
                  {share.expireInfinity
                    ? Lang.t('share.noExpire')
                    : `${Lang.t('expireTime')}：${DateUtil.simpleDateHourMinute(
                        share.expireTime
                      )}`}
                </span>
              </div>

              {/* 添加权限信息 */}
              <div className="cell-btn text">
                <span>
                  {Lang.t('share.permission')}:{' '}
                  <Tag color={this.getPermissionColor()} className="small-tag">
                    {this.getPermissionText()}
                  </Tag>
                </span>
              </div>

              {/* 添加分享对象信息 */}
              <div className="cell-btn text">
                <span>
                  {Lang.t('share.shareTarget')}: {shareTargetText}
                </span>
              </div>

              <div
                className="cell-btn"
                onClick={(e) =>
                  SafeUtil.stopPropagationWrap(e)(this.showShare())
                }
              >
                <InfoCircleOutlined className="btn-action mr5" />
                {Lang.t('share.shareDetail')}
              </div>

              {/* 添加预览按钮 */}
              <div
                className="cell-btn"
                onClick={(e) =>
                  SafeUtil.stopPropagationWrap(e)(this.handleShareClick())
                }
              >
                {share.sharePermission === 'PREVIEW_ONLY' && !isShareOwner ? (
                  <>
                    <EyeInvisibleOutlined className="btn-action mr5" />
                    {Lang.t('share.previewOnly')}
                  </>
                ) : (
                  <>
                    <EyeOutlined className="btn-action mr5" />
                    {Lang.t('matter.preview')}
                  </>
                )}
              </div>

              {showDelete && (
                <div
                  className="cell-btn red"
                  onClick={(e) =>
                    SafeUtil.stopPropagationWrap(e)(this.delShare())
                  }
                >
                  <DeleteOutlined className="btn-action" />
                  {Lang.t('delete')}
                </div>
              )}
            </div>
          ) : null}
        </Expanding>
      </div>
    );
  }
}